import * as Y from 'yjs';
import { Emitter } from './emitter';
import { hashString, shortId } from './id';
import {
  applyParamPatch,
  defaultParams,
  getNodeType,
  reconcileParams,
  requireNodeType,
} from './registry';
import {
  LOAD_ORIGIN,
  LOCAL_ORIGIN,
  createYGraph,
  edgeToY,
  nodeToY,
  paramsToY,
  yToEdge,
  yToNode,
} from './ydoc';
import type {
  DataKind,
  GraphDoc,
  FilterMode,
  GraphEdge,
  GraphNode,
  OutputChannel,
  ParamBag,
  ValidationIssue,
} from './types';

export const DOC_VERSION = 1;
export const VALID_RESOLUTIONS = [128, 256, 512, 1024, 2048] as const;

/** Declared as a type alias, not an interface: `Emitter` needs an index signature. */
export type GraphEvents = {
  /** Any structural or parameter change. Carries a coarse reason for the UI. */
  change: { reason: 'node' | 'edge' | 'param' | 'doc' | 'load' };
  /** Emitted alongside `change` when the set of nodes/edges shifted. */
  topology: void;
};

/**
 * The mutable texture graph.
 *
 * Every mutator validates before it mutates, so a rejected operation leaves the
 * document byte-identical to how it started. That invariant is what lets the
 * agent bridge retry or replay an op batch without first snapshotting state.
 */
export class Graph {
  readonly events = new Emitter<GraphEvents>();

  /**
   * The document, as the thing that is actually mutated.
   *
   * Public because the undo manager and, later, the sync layer both need it.
   * Nothing else should write to it directly — the mutators below are what
   * enforce the validation rules, and a write that bypasses them can put the
   * document into a state no operation could have produced.
   */
  readonly y = createYGraph();

  /**
   * The materialised view. Plain objects, rebuilt from `y` on every change.
   *
   * Existing node objects are updated in place rather than replaced, so a
   * reference held across an edit — a drag in progress, a selection — keeps
   * pointing at the live node. That was true before the CRDT went in and stays
   * true now.
   */
  private nodes = new Map<string, GraphNode>();
  private edges = new Map<string, GraphEdge>();
  private _name = 'Untitled';
  private _resolution = 512;
  private _filtering: FilterMode = 'linear';

  constructor() {
    this.y.doc.on('afterTransaction', (transaction: Y.Transaction) => {
      if (transaction.changed.size === 0 && transaction.changedParentTypes.size === 0) return;
      this.materialise();
      // A mutator emits its own precise reason, and `loadJSON` emits its own
      // too. Anything else — an undo, a redo, an edit arriving from another
      // client — has no caller to announce it, so it is announced here.
      if (transaction.origin !== LOCAL_ORIGIN && transaction.origin !== LOAD_ORIGIN) {
        this.events.emit('change', { reason: 'load' });
        this.events.emit('topology', undefined);
      }
    });
  }

  /**
   * Origin stamped on transactions that do not name one.
   *
   * Swapped by `withOrigin` so a whole run of ordinary mutator calls can be
   * attributed to something other than this user — loading a document, or
   * applying an edit that arrived from someone else.
   */
  private defaultOrigin: unknown = LOCAL_ORIGIN;

  /** Run `fn` as one undoable unit under the given origin. */
  transact<T>(fn: () => T, origin: unknown = this.defaultOrigin): T {
    let result!: T;
    this.y.doc.transact(() => {
      result = fn();
    }, origin);
    return result;
  }

  /** Run `fn` with every write attributed to `origin`. */
  withOrigin<T>(origin: unknown, fn: () => T): T {
    const previous = this.defaultOrigin;
    this.defaultOrigin = origin;
    try {
      return fn();
    } finally {
      this.defaultOrigin = previous;
    }
  }

  /**
   * Rebuild the plain model from the document.
   *
   * A whole-model pass rather than an incremental patch. A texture graph is a
   * few hundred nodes, so the cost is nothing next to a single render, and the
   * alternative — replaying Yjs event paths into targeted mutations — is a
   * second implementation of the same state that can drift from the first.
   */
  private materialise(): void {
    this._name = (this.y.meta.get('name') as string) ?? 'Untitled';
    const resolution = this.y.meta.get('resolution');
    if ((VALID_RESOLUTIONS as readonly number[]).includes(resolution as number)) {
      this._resolution = resolution as number;
    }
    this._filtering = this.y.meta.get('filtering') === 'nearest' ? 'nearest' : 'linear';

    const seenNodes = new Set<string>();
    for (const [id, ynode] of this.y.nodes) {
      seenNodes.add(id);
      const next = yToNode(id, ynode);
      const existing = this.nodes.get(id);
      if (!existing) {
        this.nodes.set(id, next);
        continue;
      }
      Object.assign(existing, next);
      // Assigning cannot clear a key that is now absent, and both of these are
      // optional — a node that was un-titled would keep its old title forever.
      if (next.title === undefined) delete existing.title;
      if (next.collapsed === undefined) delete existing.collapsed;
    }
    for (const id of [...this.nodes.keys()]) if (!seenNodes.has(id)) this.nodes.delete(id);

    const seenEdges = new Set<string>();
    for (const [id, yedge] of this.y.edges) {
      seenEdges.add(id);
      const next = yToEdge(id, yedge);
      const existing = this.edges.get(id);
      if (existing) Object.assign(existing, next);
      else this.edges.set(id, next);
    }
    for (const id of [...this.edges.keys()]) if (!seenEdges.has(id)) this.edges.delete(id);
  }

  get name(): string {
    return this._name;
  }

  get resolution(): number {
    return this._resolution;
  }

  setName(name: string): void {
    this.transact(() => this.y.meta.set('name', name.trim() || 'Untitled'));
    this.events.emit('change', { reason: 'doc' });
  }

  get filtering(): FilterMode {
    return this._filtering;
  }

  /**
   * Linear smooths between texels; nearest keeps them square.
   *
   * Nearest is what a low-resolution document wants — at 128px, linear
   * filtering blurs away the very pixel structure that is the point.
   */
  setFiltering(mode: FilterMode): void {
    if (mode !== 'linear' && mode !== 'nearest') {
      throw new Error(`Filtering must be "linear" or "nearest", received "${mode}"`);
    }
    if (mode === this._filtering) return;
    this.transact(() => this.y.meta.set('filtering', mode));
    this.events.emit('change', { reason: 'doc' });
  }

  setResolution(res: number): void {
    if (!(VALID_RESOLUTIONS as readonly number[]).includes(res)) {
      throw new Error(`Resolution must be one of ${VALID_RESOLUTIONS.join(', ')}, received ${res}`);
    }
    if (res === this._resolution) return;
    this.transact(() => this.y.meta.set('resolution', res));
    this.events.emit('change', { reason: 'doc' });
  }

  // ---------------------------------------------------------------- queries

  getNode(id: string): GraphNode | undefined {
    return this.nodes.get(id);
  }

  requireNode(id: string): GraphNode {
    const n = this.nodes.get(id);
    if (!n) throw new Error(`No node with id "${id}"`);
    return n;
  }

  listNodes(): GraphNode[] {
    return [...this.nodes.values()];
  }

  listEdges(): GraphEdge[] {
    return [...this.edges.values()];
  }

  get nodeCount(): number {
    return this.nodes.size;
  }

  get edgeCount(): number {
    return this.edges.size;
  }

  /** Edges terminating at `nodeId`, optionally narrowed to one input port. */
  incoming(nodeId: string, port?: string): GraphEdge[] {
    return this.listEdges().filter(
      (e) => e.to === nodeId && (port === undefined || e.toPort === port),
    );
  }

  /** Edges originating at `nodeId`. */
  outgoing(nodeId: string): GraphEdge[] {
    return this.listEdges().filter((e) => e.from === nodeId);
  }

  // --------------------------------------------------------------- mutators

  addNode(
    type: string,
    opts: { x?: number; y?: number; params?: ParamBag; id?: string } = {},
  ): GraphNode {
    const def = requireNodeType(type);
    const id = opts.id ?? shortId(type.replace(/^.*\./, ''));
    if (this.nodes.has(id)) throw new Error(`A node with id "${id}" already exists`);
    const node: GraphNode = {
      id,
      type,
      x: opts.x ?? 0,
      y: opts.y ?? 0,
      params: opts.params ? reconcileParams(def, opts.params) : defaultParams(def),
    };
    this.transact(() => this.y.nodes.set(id, nodeToY(node)));
    this.emitChange('node', true);
    return this.nodes.get(id)!;
  }

  removeNode(id: string): void {
    if (!this.nodes.has(id)) throw new Error(`No node with id "${id}"`);
    // One transaction, so undoing a delete brings the edges back with it.
    this.transact(() => {
      this.y.nodes.delete(id);
      for (const [edgeId, edge] of [...this.edges]) {
        if (edge.from === id || edge.to === id) this.y.edges.delete(edgeId);
      }
    });
    this.emitChange('node', true);
  }

  moveNode(id: string, x: number, y: number): void {
    this.requireNode(id);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      throw new Error(`Node position must be finite, received (${x}, ${y})`);
    }
    this.transact(() => {
      const ynode = this.y.nodes.get(id)!;
      ynode.set('x', x);
      ynode.set('y', y);
    });
    // Position does not affect evaluation; the UI redraws, the engine does not.
    this.events.emit('change', { reason: 'node' });
  }

  setNodeTitle(id: string, title: string | undefined): void {
    this.requireNode(id);
    const trimmed = title?.trim();
    this.transact(() => {
      const ynode = this.y.nodes.get(id)!;
      if (trimmed) ynode.set('title', trimmed);
      else ynode.delete('title');
    });
    this.events.emit('change', { reason: 'node' });
  }

  setNodeCollapsed(id: string, collapsed: boolean): void {
    this.requireNode(id);
    this.transact(() => {
      const ynode = this.y.nodes.get(id)!;
      if (collapsed) ynode.set('collapsed', true);
      else ynode.delete('collapsed');
    });
    this.events.emit('change', { reason: 'node' });
  }

  setParams(id: string, patch: Record<string, unknown>): GraphNode {
    const node = this.requireNode(id);
    const def = requireNodeType(node.type);
    // Build the next bag first; a throw here must not leave a half-applied patch.
    const next = applyParamPatch(def, node.params, patch);
    // Written key by key rather than as one value, which is what lets two
    // people adjust different parameters of one node without fighting.
    this.transact(() => {
      const yparams = this.y.nodes.get(id)!.get('params') as Y.Map<unknown>;
      for (const [key, value] of Object.entries(next)) {
        if (JSON.stringify(yparams.get(key)) === JSON.stringify(value)) continue;
        yparams.set(key, structuredClone(value));
      }
      for (const key of [...yparams.keys()]) if (!(key in next)) yparams.delete(key);
    });
    this.emitChange('param', false);
    return this.nodes.get(id)!;
  }

  resetParams(id: string): void {
    const node = this.requireNode(id);
    const defaults = defaultParams(requireNodeType(node.type));
    this.transact(() => this.y.nodes.get(id)!.set('params', paramsToY(defaults)));
    this.emitChange('param', false);
  }

  /**
   * Connect `from` → `to.toPort`.
   *
   * Input ports accept a single edge, so an existing connection on the target
   * port is replaced. Returns the new edge.
   */
  connect(from: string, to: string, toPort: string, fromPort = 'out'): GraphEdge {
    const src = this.requireNode(from);
    const dst = this.requireNode(to);
    const dstDef = requireNodeType(dst.type);

    if (from === to) throw new Error('A node cannot connect to itself');
    if (!dstDef.inputs.some((i) => i.name === toPort)) {
      const valid = dstDef.inputs.map((i) => i.name).join(', ');
      throw new Error(
        `Node type "${dstDef.id}" has no input "${toPort}". Valid inputs: ${valid || '(none)'}`,
      );
    }
    if (this.wouldCycle(src.id, dst.id)) {
      throw new Error(`Connecting "${from}" → "${to}" would create a cycle`);
    }

    const edge: GraphEdge = { id: shortId('e'), from, fromPort, to, toPort };
    // Replacing what was on the port and adding the new edge are one step, so
    // undo cannot leave the port empty.
    this.transact(() => {
      for (const existing of this.incoming(to, toPort)) this.y.edges.delete(existing.id);
      this.y.edges.set(edge.id, edgeToY(edge));
    });
    this.emitChange('edge', true);
    return this.edges.get(edge.id)!;
  }

  /** Clear one input port. No-op semantics are an error: the agent should know. */
  disconnect(to: string, toPort: string): void {
    this.requireNode(to);
    const existing = this.incoming(to, toPort);
    if (!existing.length) throw new Error(`Input "${toPort}" on "${to}" is not connected`);
    this.transact(() => {
      for (const e of existing) this.y.edges.delete(e.id);
    });
    this.emitChange('edge', true);
  }

  clear(): void {
    this.transact(() => {
      this.y.nodes.clear();
      this.y.edges.clear();
    });
    this.emitChange('doc', true);
  }

  // -------------------------------------------------------------- traversal

  /** True if adding `from` → `to` would close a loop. */
  wouldCycle(from: string, to: string): boolean {
    if (from === to) return true;
    // Walk upstream from `from`; if we reach `to`, the new edge closes a cycle.
    const stack = [from];
    const seen = new Set<string>();
    while (stack.length) {
      const cur = stack.pop()!;
      if (cur === to) return true;
      if (seen.has(cur)) continue;
      seen.add(cur);
      for (const e of this.incoming(cur)) stack.push(e.from);
    }
    return false;
  }

  /**
   * Nodes in dependency order (inputs before consumers).
   *
   * Kahn's algorithm. The graph cannot contain a cycle — `connect` refuses to
   * create one — but any node left with unmet dependencies is dropped rather
   * than throwing, so a corrupted imported file still renders what it can.
   */
  topoOrder(): GraphNode[] {
    const indegree = new Map<string, number>();
    for (const n of this.nodes.keys()) indegree.set(n, 0);
    for (const e of this.edges.values()) {
      if (this.nodes.has(e.to) && this.nodes.has(e.from)) {
        indegree.set(e.to, (indegree.get(e.to) ?? 0) + 1);
      }
    }
    const ready = [...indegree].filter(([, d]) => d === 0).map(([id]) => id);
    const order: GraphNode[] = [];
    while (ready.length) {
      const id = ready.shift()!;
      const node = this.nodes.get(id);
      if (node) order.push(node);
      for (const e of this.outgoing(id)) {
        const d = (indegree.get(e.to) ?? 0) - 1;
        indegree.set(e.to, d);
        if (d === 0) ready.push(e.to);
      }
    }
    return order;
  }

  /** Every node that `id` transitively depends on, plus `id` itself, in order. */
  subgraphFor(id: string): GraphNode[] {
    const needed = new Set<string>();
    const stack = [id];
    while (stack.length) {
      const cur = stack.pop()!;
      if (needed.has(cur)) continue;
      needed.add(cur);
      for (const e of this.incoming(cur)) stack.push(e.from);
    }
    return this.topoOrder().filter((n) => needed.has(n.id));
  }

  /**
   * The concrete kind a node emits, resolving `passthrough` declarations.
   *
   * Walks upstream through channel-agnostic nodes until it reaches one that
   * commits to a kind. An unconnected or cyclic-looking chain falls back to
   * `color`, the permissive answer, so resolution never reports a false
   * mismatch on an incomplete graph.
   */
  resolveOutputKind(nodeId: string, seen = new Set<string>()): DataKind {
    if (seen.has(nodeId)) return 'color';
    seen.add(nodeId);

    const node = this.nodes.get(nodeId);
    const def = node && getNodeType(node.type);
    if (!def) return 'color';
    if (def.output !== 'passthrough') return def.output;

    const port = def.passthroughFrom ?? def.inputs[0]?.name;
    if (!port) return 'color';
    const edge = this.incoming(nodeId, port)[0];
    return edge ? this.resolveOutputKind(edge.from, seen) : 'color';
  }

  /** Output nodes keyed by the channel they drive. */
  outputs(): Map<OutputChannel, GraphNode> {
    const map = new Map<OutputChannel, GraphNode>();
    for (const node of this.nodes.values()) {
      const def = getNodeType(node.type);
      if (def?.category !== 'output') continue;
      map.set(node.params.channel as OutputChannel, node);
    }
    return map;
  }

  // ------------------------------------------------------------- validation

  validate(): ValidationIssue[] {
    const issues: ValidationIssue[] = [];

    for (const node of this.nodes.values()) {
      const def = getNodeType(node.type);
      if (!def) {
        issues.push({
          severity: 'error',
          nodeId: node.id,
          message: `Unknown node type "${node.type}"`,
        });
        continue;
      }
      for (const input of def.inputs) {
        if (input.optional) continue;
        if (!this.incoming(node.id, input.name).length) {
          issues.push({
            severity: 'error',
            nodeId: node.id,
            message: `Required input "${input.label}" is not connected`,
          });
        }
      }
      // Feeding colour into a port that expects a single channel is legal but
      // almost never intended; flag it rather than silently taking the red channel.
      for (const edge of this.incoming(node.id)) {
        const port = def.inputs.find((i) => i.name === edge.toPort);
        if (port && port.kind === 'grayscale' && this.resolveOutputKind(edge.from) === 'color') {
          issues.push({
            severity: 'warning',
            nodeId: node.id,
            edgeId: edge.id,
            message: `Colour output feeds grayscale input "${port.label}"; only luminance is used`,
          });
        }
      }
    }

    const outputs = new Map<string, string[]>();
    for (const node of this.nodes.values()) {
      if (getNodeType(node.type)?.category !== 'output') continue;
      const ch = String(node.params.channel);
      outputs.set(ch, [...(outputs.get(ch) ?? []), node.id]);
    }
    for (const [channel, ids] of outputs) {
      if (ids.length > 1) {
        for (const id of ids) {
          issues.push({
            severity: 'warning',
            nodeId: id,
            message: `${ids.length} output nodes target "${channel}"; the last one in evaluation order wins`,
          });
        }
      }
    }

    if (this.nodes.size && !outputs.size) {
      issues.push({
        severity: 'warning',
        message: 'Graph has no Output node, so it produces no material channels',
      });
    }

    return issues;
  }

  // ------------------------------------------------------------ persistence

  /**
   * A standalone copy of the document.
   *
   * Deep, and that is the whole point: undo history and autosave both hold the
   * result as a snapshot while the graph goes on being edited. `moveNode` and
   * `setParams` write to the node objects in place, so handing out references
   * gave every stored snapshot a live view of the current document — undo
   * restored the state it had just captured and appeared to do nothing at all.
   */
  toJSON(): GraphDoc {
    return structuredClone({
      version: DOC_VERSION,
      name: this._name,
      resolution: this._resolution,
      filtering: this._filtering,
      // Sort for stable diffs — documents land in git far more often than not.
      nodes: this.listNodes().sort((a, b) => a.id.localeCompare(b.id)),
      edges: this.listEdges().sort((a, b) => a.id.localeCompare(b.id)),
    });
  }

  /**
   * Replace the document wholesale.
   *
   * Written under the load origin rather than the local one, so opening a file
   * is not something the undo stack knows about — undoing straight after an
   * open would otherwise restore whatever happened to be on screen before.
   */
  loadJSON(doc: GraphDoc): void {
    this.transact(() => {
      this.y.nodes.clear();
      this.y.edges.clear();
      this.y.meta.set('name', doc.name ?? 'Untitled');
      this.y.meta.set(
        'resolution',
        (VALID_RESOLUTIONS as readonly number[]).includes(doc.resolution) ? doc.resolution : 512,
      );
      this.y.meta.set('filtering', doc.filtering === 'nearest' ? 'nearest' : 'linear');

      const added = new Set<string>();
      for (const n of doc.nodes ?? []) {
        const def = getNodeType(n.type);
        // Preserve unknown types verbatim so a document authored against a
        // newer build round-trips instead of losing nodes; `validate` reports
        // them. reconcileParams rebuilds every value it knows about, so a known
        // type is already the graph's own; an unknown one is cloned on the way
        // into the document rather than shared with the caller.
        this.y.nodes.set(
          n.id,
          nodeToY({
            ...n,
            params: def ? reconcileParams(def, n.params ?? {}) : (n.params ?? {}),
          }),
        );
        added.add(n.id);
      }
      for (const e of doc.edges ?? []) {
        if (!added.has(e.from) || !added.has(e.to)) continue;
        this.y.edges.set(e.id, edgeToY({ ...e, fromPort: e.fromPort ?? 'out' }));
      }
    }, LOAD_ORIGIN);
    this.events.emit('change', { reason: 'load' });
    this.events.emit('topology', undefined);
  }

  /**
   * Content hash of everything that affects rendering.
   * Node positions and titles are deliberately excluded.
   */
  renderHash(): number {
    const parts: string[] = [String(this._resolution), this._filtering];
    for (const n of this.topoOrder()) {
      parts.push(n.id, n.type, JSON.stringify(n.params));
      for (const e of this.incoming(n.id).sort((a, b) => a.toPort.localeCompare(b.toPort))) {
        parts.push(e.toPort, e.from);
      }
    }
    return hashString(parts.join('|'));
  }

  private emitChange(reason: GraphEvents['change']['reason'], topology: boolean): void {
    this.events.emit('change', { reason });
    if (topology) this.events.emit('topology', undefined);
  }
}
